import { datadogLogs } from '@datadog/browser-logs';
import { logContext } from '@shared/services';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  isAxiosError,
} from 'axios';

// Constants
const DEFAULT_TIMEOUT = 30000;

// TypeScript types
export interface ApiClientConfig {
  baseURL?: string;
  timeout?: number;
}

export interface TokenProvider {
  getToken: (options?: { skipCache?: boolean }) => Promise<string | null>;
}

type AuthenticatedAxiosRequestConfig = AxiosRequestConfig & {
  _interceptorRetryCount?: number;
};

const getContentType = (headers: unknown): string =>
  ((headers as Record<string, string>)?.['content-type'] ?? '').toLowerCase();

const isHtmlAuthResponse = (error: AxiosError): boolean => {
  if (!error.response) return false;
  const { status, headers, data } = error.response;
  if (status !== 401 && status !== 403) return false;

  const contentType = getContentType(headers);
  if (contentType.startsWith('text/html')) return true;

  return typeof data === 'string' && data.trimStart().startsWith('<');
};

export const getInterceptorRetryCount = (error: unknown): number => {
  if (!isAxiosError(error)) return 0;
  const config = error.config as AuthenticatedAxiosRequestConfig | undefined;
  return config?._interceptorRetryCount ?? 0;
};

/**
 * Authenticated API Client with automatic token management
 * Handles expired tokens automatically by refreshing and retrying requests
 */
export class AuthenticatedApiClient {
  private axiosInstance: AxiosInstance;
  private tokenProvider: TokenProvider;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(tokenProvider: TokenProvider, config: ApiClientConfig = {}) {
    this.tokenProvider = tokenProvider;

    // Create axios instance with default config
    this.axiosInstance = axios.create({
      baseURL: config.baseURL || '/api',
      timeout: config.timeout || DEFAULT_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Setup interceptor
    this.setupResponseInterceptor();
  }

  private setupResponseInterceptor(): void {
    const maxInterceptorRetries = 2;

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (!error.response || !error.config) return Promise.reject(error);

        const originalRequest = error.config as AuthenticatedAxiosRequestConfig;
        const { status } = error.response;
        const retryCount: number = originalRequest._interceptorRetryCount ?? 0;

        const is405 = status === 405;
        const isHtmlAuth = isHtmlAuthResponse(error);
        const willRetry =
          (is405 || isHtmlAuth) && retryCount < maxInterceptorRetries;

        // TODO: Remove after ~3 days post-deploy (added 2026-07-01).
        // Verifies HTML heuristic accuracy in prod.
        const shouldLogDiagnostic =
          is405 ||
          isHtmlAuth ||
          ((status === 401 || status === 403) &&
            typeof error.response.data === 'string' &&
            !getContentType(error.response.headers).includes('application/json'));

        if (shouldLogDiagnostic) {
          const contentType = getContentType(error.response.headers);
          const rawBody: unknown = error.response.data;
          const bodyPreview =
            typeof rawBody === 'string'
              ? rawBody.slice(0, 200)
              : '[non-string body]';

          datadogLogs.logger.info(
            'Auth/method error interceptor diagnostic',
            logContext('api-client', 'interceptor-diag', {
              detail:
                'Temporary: verify HTML heuristic accuracy in prod before removing',
              statusCode: status,
              url: originalRequest.url,
              method: originalRequest.method?.toUpperCase(),
              contentType,
              bodyPreview,
              bodyType: typeof rawBody,
              willRetry,
              interceptorAttempt: retryCount + 1,
            }),
          );
        }

        if (willRetry) {
          datadogLogs.logger.info(
            'Token refresh and retry after auth/method error',
            logContext('api-client', 'interceptor-retry', {
              detail:
                'Refreshing Clerk session cookie and replaying the request',
              statusCode: status,
              url: originalRequest.url,
              method: originalRequest.method?.toUpperCase(),
              interceptorAttempt: retryCount + 1,
              maxInterceptorRetries,
            }),
          );

          // forceTokenRefresh returns false on any failure (no throw).
          const sessionRefreshed = await this.forceTokenRefresh();

          if (sessionRefreshed) {
            // Bump only on dispatch — failed refresh shouldn't burn a slot.
            originalRequest._interceptorRetryCount = retryCount + 1;
            return this.axiosInstance(originalRequest);
          }

          datadogLogs.logger.error(
            'Token refresh failed during interceptor retry',
            logContext('api-client', 'token-refresh', {
              detail:
                'getToken({ skipCache: true }) returned null or threw — session likely expired',
              url: originalRequest.url,
              method: originalRequest.method?.toUpperCase(),
              interceptorAttempt: retryCount + 1,
            }),
          );
        } else if (is405 || isHtmlAuth) {
          datadogLogs.logger.info(
            'Interceptor max retries reached, passing error to React Query',
            logContext('api-client', 'interceptor-max-retries', {
              detail:
                'All interceptor refresh attempts exhausted; React Query retry takes over',
              statusCode: status,
              url: originalRequest.url,
              method: originalRequest.method?.toUpperCase(),
              maxInterceptorRetries,
            }),
          );
        }

        return Promise.reject(error);
      },
    );
  }

  /**
   * Force token refresh using Clerk's getToken({ skipCache: true })
   * Implements request queuing to prevent multiple simultaneous refresh attempts
   */
  private async forceTokenRefresh(): Promise<boolean> {
    // If refresh is already in progress, return the existing promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start new refresh process
    this.refreshPromise = this.doTokenRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      // Clear the promise after completion (success or failure)
      this.refreshPromise = null;
    }
  }

  /**
   * Actual token refresh implementation.
   *
   * Uses getToken({ skipCache: true }) instead of session.reload(). Both mint a
   * fresh JWT and sync the __session cookie, but skipCache does NOT rehydrate the
   * User object — avoiding a Clerk race condition where useAuth() throws
   * "Invalid state" mid-render (@clerk/nextjs 6.39.4), crashing the client.
   * See ECX-2580 follow-up: session.reload() caused 100% of observed
   * "Invalid state" client crashes in production.
   */
  private async doTokenRefresh(): Promise<boolean> {
    try {
      // TODO(clerk-core-v3): Revisit this when upgrading to Clerk Core 3+
      // (@clerk/nextjs v7). Two behaviors change and affect this method:
      //   1. getToken() throws ClerkOfflineError on network failure instead of
      //      returning null. Our catch below already turns that into `false`
      //      (no retry), but if we want offline-specific handling, branch here.
      //   2. v7 exposes a standalone getToken() built for API interceptors
      //      (import { getToken } from '@clerk/nextjs') plus stale-while-
      //      revalidate — consider using it instead of threading getToken
      //      through TokenProvider/useAuth.
      // NOTE: v7 does NOT fix the resolveAuthState "Invalid state" gap, so keep
      // using getToken({ skipCache: true }) — never switch back to
      // session.reload()/user.reload() here (that rehydration is what crashed
      // useAuth mid-render; see the class-level comment on doTokenRefresh).
      const token = await this.tokenProvider.getToken({ skipCache: true });
      const refreshed = Boolean(token);

      datadogLogs.logger.info(
        refreshed
          ? 'Clerk session token refreshed successfully'
          : 'Clerk token refresh returned no token',
        logContext('api-client', 'token-refresh', {
          detail: refreshed
            ? 'Fresh __session cookie available for subsequent requests'
            : 'getToken({ skipCache: true }) returned null — session likely expired',
          refreshed,
        }),
      );

      return refreshed;
    } catch (error) {
      datadogLogs.logger.error(
        'Failed to force token refresh',
        logContext('api-client', 'token-refresh', {
          detail: 'getToken({ skipCache: true }) threw unexpectedly',
          error: error instanceof Error ? error.message : String(error),
        }),
      );
      return false;
    }
  }

  // Public API methods
  public get<T = unknown>(url: string, config?: AxiosRequestConfig) {
    return this.axiosInstance.get<T>(url, config);
  }

  public post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ) {
    return this.axiosInstance.post<T>(url, data, config);
  }

  public put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ) {
    return this.axiosInstance.put<T>(url, data, config);
  }

  public delete<T = unknown>(url: string, config?: AxiosRequestConfig) {
    return this.axiosInstance.delete<T>(url, config);
  }

  public patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ) {
    return this.axiosInstance.patch<T>(url, data, config);
  }
}
